// TextInput.jsx

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// CSS
import './TextInput.css';

// Icons
import Icon from 'svg/Icon';

/**
 * TextInput
 *
 * A versatile text input component supporting single-line and multiline modes, with built-in features like character 
 * counting, auto-expanding textareas, password visibility toggling, and clear actions. It also includes enhanced handling 
 * to prevent parent draggable elements from interfering with text selection interactions.
 *
 * Native properties used in this component:
 * @param {string} className - Additional CSS class for the input container.
 * @param {boolean} disabled - Whether the input is disabled.
 * @param {number} maxLength - Maximum character length allowed.
 * @param {boolean} readOnly - Whether the input is read-only.
 * @param {object} style - Inline styles for the input container.
 * @param {string} type - Native input type (e.g., 'text', 'password').
 * @param {string|number} value - Controlled input value.
 * 
 * Native event handlers used in this component:
 * @param {function} onBlur - Handler for blur events.
 * @param {function} onChange - Handler for change events.
 * @param {function} onDragStart - Handler for native drag start events.
 * @param {function} onFocus - Handler for focus events.
 * @param {function} onInput - Handler for input events.
 * @param {function} onKeyDown - Handler for keydown events.
 * @param {function} onPointerCancel - Handler for pointer cancel events.
 * @param {function} onPointerDown - Handler for pointer down events.
 * @param {function} onPointerUp - Handler for pointer up events.
 * 
 * Custom properties:
 * @param {boolean} autoSelect - Whether to auto-select the input content on mount.
 * @param {boolean} clearable - Whether to show a clear action when there is content.
 * @param {React.Component|function} endIcon - Custom end icon component or render function.
 * @param {string} endIconColor - Color for the end icon.
 * @param {string} endIconKey - Icon key for the end icon when using the built-in Icon component.
 * @param {boolean} error - Whether the input is in an error state, affecting styling.
 * @param {boolean} expandable - Whether a multiline textarea should expand in height with content.
 * @param {string|number} height - Fixed height for the input container (overridden by autosize when enabled).
 * @param {string} helperText - Optional helper text displayed below the input.
 * @param {boolean} hybrid - Whether to use hybrid styling (e.g., for dark backgrounds).
 * @param {number} iconSize - Size for start and end icons.
 * @param {boolean} multiline - Whether to render a textarea instead of an input.
 * @param {boolean} showCount - Whether to show character count when maxLength is provided.
 * @param {boolean} showPasswordToggle - Whether to show a password visibility toggle in password mode.
 * @param {React.Component|function} startIcon - Custom start icon component or render function.
 * @param {string} startIconColor - Color for the start icon.
 * @param {string} startIconKey - Icon key for the start icon when using the built-in Icon component.
 * @param {string|number} width - Width of the input container.
 * 
 * Custom event handlers:
 * @param {function} onClear - Handler for clear action clicks.
 * @param {function} onEnterPress - Handler for Enter key presses (only in single-line mode).
 * @param {function} onEscapePress - Handler for Escape key presses.
 *
 * Native Passthrough:
 * @param {object} rest - Additional native input or textarea props.
 */
const TextInput = forwardRef(({

    // Native properties used in this component
    className = '',
    disabled = false,
    maxLength,
    readOnly = false,
    style = {},
    type = 'text',
    value = '',

    // Native event handlers used in this component
    onBlur,
    onChange,
    onDragStart,
    onFocus,
    onInput,
    onKeyDown,
    onPointerCancel,
    onPointerDown,
    onPointerUp,

    // Custom properties
    autoSelect = false,
    clearable = false,
    endIcon: EndIcon,
    endIconColor,
    endIconKey,
    error = false,
    expandable = false,
    height = 'auto',
    helperText = '',
    hybrid = false,
    iconSize = 20,
    multiline = false,
    showCount = false,
    showPasswordToggle = true,
    startIcon: StartIcon,
    startIconColor,
    startIconKey,
    width = '100%',

    // Custom event handlers
    onClear,
    onEnterPress,
    onEscapePress,

    // Native Passthrough
    ...rest

}, ref) => {

    // ----- STATE ----- //

    // Character Count State
    const [charCount, setCharCount] = useState((value || '').length);

    // Autosize Activation State
    const [autosizeActive, setAutosizeActive] = useState(false);

    // Password Visibility State
    const [passwordVisible, setPasswordVisible] = useState(false);

    // ----- MEMOS ----- //

    // Determines whether password toggle can be shown without UI collisions.
    const shouldShowPasswordToggle = useMemo(() => {
        if (!showPasswordToggle) return false;
        if (multiline) return false;
        if (type !== 'password') return false;
        if (EndIcon || endIconKey) return false;
        if (clearable) return false;
        return true;
    }, [showPasswordToggle, multiline, type, EndIcon, endIconKey, clearable]);

    // Resolves final native input type based on password visibility.
    const effectiveInputType = useMemo(() => {
        if (multiline) return undefined;
        if (type === 'password') return passwordVisible ? 'text' : 'password';
        return type;
    }, [multiline, type, passwordVisible]);

    // Memoized character count derived from controlled value.
    const memoizedCharCount = useMemo(() => (value || '').length, [value]);

    // Indicates if a start icon is rendered.
    const hasStartIcon = useMemo(() => Boolean(StartIcon || startIconKey), [StartIcon, startIconKey]);

    // Indicates if an end icon is rendered.
    const hasEndIcon = useMemo(() => Boolean(EndIcon || endIconKey), [EndIcon, endIconKey]);

    // Dynamic left and right padding based on icon and action affordances.
    const dynamicPadding = useMemo(() => ({
        paddingLeft: hasStartIcon ? 40 : 14,
        paddingRight: (hasEndIcon || (clearable && value) || shouldShowPasswordToggle) ? 40 : 14,
    }), [hasStartIcon, hasEndIcon, clearable, value, shouldShowPasswordToggle]);

    // Memoized max height parsed from prop or style override.
    const maxHeightValue = useMemo(() => {
        const propMax = typeof rest.maxHeight === 'number' ? rest.maxHeight : undefined;
        const styleMax = (() => {
            const m = style?.maxHeight;
            if (m == null) return undefined;
            if (typeof m === 'number') return m;
            const n = parseFloat(m);
            return Number.isFinite(n) ? n : undefined;
        })();
        return propMax ?? styleMax;
    }, [rest.maxHeight, style?.maxHeight]);

    // Wrapper class for disabled styling.
    const wrapperClass = useMemo(() => `text-input-wrapper${disabled ? ' disabled' : ''}`, [disabled]);

    // Base input class with error and hybrid variants.
    const baseClass = useMemo(() => {
        return `text-input-container${error ? ' text-input-error' : ''}${hybrid ? ' text-input-hybrid' : ''} ${className}`.trim();
    }, [error, hybrid, className]);

    // ----- UTILITIES ----- //

    const effectiveStartIconColor = startIconColor ?? 'currentColor';
    const effectiveEndIconColor = endIconColor ?? 'currentColor';

    const internalRef = useRef(null);
    const inputRef = ref || internalRef;

    const otherRest = rest;

    // Autosize helper that clamps to maxHeight when provided.
    const autoSizeNow = useCallback((el) => {
        if (!el) return;
        el.style.height = 'auto';
        const max = maxHeightValue;
        const h = el.scrollHeight;
        el.style.height = `${max ? Math.min(h, max) : h}px`;
        el.style.overflow = max && h > max ? 'auto hidden' : 'hidden';
    }, [maxHeightValue]);

    // ----- EFFECTS ----- //

    // Keeps textarea sizing and count in sync with controlled external value updates.
    useEffect(() => {
        if (!multiline || !expandable || !inputRef.current) {
            setCharCount(memoizedCharCount);
            return;
        }

        const isNonEmpty = Boolean(value && String(value).length > 0);

        // If consumer sets a non-empty value, flip autosize on and size immediately.
        if (isNonEmpty && !autosizeActive) {
            setAutosizeActive(true);
            // size on next frame to ensure layout has settled
            requestAnimationFrame(() => autoSizeNow(inputRef.current));
        } else if (autosizeActive) {
            // Already active: keep sizing on any value change
            autoSizeNow(inputRef.current);
        }

        setCharCount(memoizedCharCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, multiline, expandable, maxHeightValue, inputRef, autosizeActive, memoizedCharCount]);

    // Selects the full input value on mount when enabled.
    useEffect(() => {
        if (autoSelect && inputRef.current) {
            inputRef.current.select();
        }
    }, [autoSelect, inputRef]);

    // ----- HANDLERS ----- //

    /**
     * Handles keydown events and forwards Enter and Escape actions.
     *
     * @param {KeyboardEvent} e - Native keyboard event.
     */
    const handleKeyDown = useCallback((e) => {
        if (!multiline && e.key === 'Enter' && onEnterPress) {
            onEnterPress(e);
        }
        if (e.key === 'Escape' && onEscapePress) {
            onEscapePress(e);
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    }, [multiline, onEnterPress, onEscapePress, onKeyDown]);

    /**
     * Enables autosize on first focus when multiline expansion is enabled.
     *
     * @param {FocusEvent} e - Native focus event.
     */
    const handleFocusInternal = useCallback((e) => {
        if (multiline && expandable && !autosizeActive) {
            setAutosizeActive(true);
            requestAnimationFrame(() => autoSizeNow(e.currentTarget));
        }
        if (onFocus) onFocus(e);
    }, [multiline, expandable, autosizeActive, autoSizeNow, onFocus]);

    /**
     * Handles native input events and keeps autosizing textareas in sync.
     *
     * @param {FormEvent} e - Native input event.
     */
    const handleInputInternal = useCallback((e) => {
        if (autosizeActive) {
            autoSizeNow(e.currentTarget);
        }

        if (onInput) {
            onInput(e);
        }
    }, [autosizeActive, autoSizeNow, onInput]);

    /**
     * Handles clear action clicks.
     */
    const handleClear = useCallback(() => {
        if (onClear) onClear();
    }, [onClear]);

    /**
     * Toggles password visibility in password mode.
     */
    const handlePasswordToggle = useCallback(() => {
        setPasswordVisible((v) => !v);
    }, []);

    const disabledDragAncestorRef = useRef(null);

    /**
     * Disables the nearest draggable ancestor while pointer selection is active.
     *
     * @param {PointerEvent} event - Native pointer down event.
     */
    const disableNearestDraggableAncestor = useCallback((event) => {
        const target = event?.target;
        if (!(target instanceof Element)) return;

        const draggableAncestor = target.closest('[draggable="true"]');
        if (!(draggableAncestor instanceof HTMLElement)) return;

        draggableAncestor.setAttribute('draggable', 'false');
        draggableAncestor.setAttribute('data-text-input-draggable-disabled', 'true');
        disabledDragAncestorRef.current = draggableAncestor;
    }, []);

    /**
     * Restores previously disabled draggable ancestor.
     */
    const restoreNearestDraggableAncestor = useCallback(() => {
        const node = disabledDragAncestorRef.current;
        if (!(node instanceof HTMLElement)) return;

        if (node.getAttribute('data-text-input-draggable-disabled') === 'true') {
            node.setAttribute('draggable', 'true');
            node.removeAttribute('data-text-input-draggable-disabled');
        }

        disabledDragAncestorRef.current = null;
    }, []);

    /**
     * Begins text-selection interaction without allowing parent row drag.
     *
     * @param {PointerEvent} event - Native pointer down event.
     */
    const handlePointerDownInternal = useCallback((event) => {
        disableNearestDraggableAncestor(event);
        if (onPointerDown) onPointerDown(event);
    }, [disableNearestDraggableAncestor, onPointerDown]);

    /**
     * Ends selection interaction and restores parent drag.
     *
     * @param {PointerEvent} event - Native pointer up/cancel event.
     */
    const handlePointerUpOrCancelInternal = useCallback((event) => {
        restoreNearestDraggableAncestor();
        if (event?.type === 'pointercancel') {
            if (onPointerCancel) onPointerCancel(event);
            return;
        }
        if (onPointerUp) onPointerUp(event);
    }, [onPointerCancel, onPointerUp, restoreNearestDraggableAncestor]);

    /**
     * Restores draggable state and forwards blur.
     *
     * @param {FocusEvent} event - Native blur event.
     */
    const handleBlurInternal = useCallback((event) => {
        restoreNearestDraggableAncestor();
        if (onBlur) onBlur(event);
    }, [onBlur, restoreNearestDraggableAncestor]);

    /**
     * Prevents native drag behavior so parent draggable field rows do not capture text selection drags.
     *
     * @param {DragEvent} event - Native drag start event.
     */
    const handleNativeDragStart = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();

        if (onDragStart) {
            onDragStart(event);
        }
    }, [onDragStart]);

    // ----- RENDER ----- //

    return (
        <div
            className={wrapperClass}
            data-no-field-drag="true"
            onDragStart={handleNativeDragStart}
            style={{
                width,
            }}
        >
            <div className={`text-input-inner${hasStartIcon ? ' has-start' : ''}${(hasEndIcon || clearable || shouldShowPasswordToggle) ? ' has-end' : ''}`}>
                {hasStartIcon && (
                    <div className="text-input-icon start">
                        {startIconKey ? (
                            <Icon
                                color={effectiveStartIconColor}
                                height={iconSize}
                                iconKey={startIconKey}
                                width={iconSize}
                            />
                        ) : (
                            <StartIcon
                                color={effectiveStartIconColor}
                                height={iconSize}
                                width={iconSize}
                            />
                        )}
                    </div>
                )}

                {multiline ? (
                    <textarea
                        className={baseClass}
                        style={{
                            ...dynamicPadding,
                            ...style,
                            ...(expandable && !autosizeActive ? { height: '40px', overflow: 'hidden' } : null),
                            resize: 'none',
                        }}
                        disabled={disabled}
                        maxLength={maxLength}
                        readOnly={readOnly}
                        ref={inputRef}
                        rows={1}
                        value={value}
                        {...otherRest}
                        onChange={onChange}
                        onDragStart={handleNativeDragStart}
                        onBlur={handleBlurInternal}
                        onFocus={handleFocusInternal}
                        onInput={handleInputInternal}
                        onKeyDown={handleKeyDown}
                        onPointerCancel={handlePointerUpOrCancelInternal}
                        onPointerDown={handlePointerDownInternal}
                        onPointerUp={handlePointerUpOrCancelInternal}
                    />
                ) : (
                    <input
                        className={baseClass}
                        style={{
                            ...dynamicPadding,
                            ...style,
                        }}
                        disabled={disabled}
                        maxLength={maxLength}
                        readOnly={readOnly}
                        ref={inputRef}
                        type={effectiveInputType}
                        value={value}
                        {...otherRest}
                        onChange={onChange}
                        onDragStart={handleNativeDragStart}
                        onBlur={handleBlurInternal}
                        onInput={onInput}
                        onKeyDown={handleKeyDown}
                        onPointerCancel={handlePointerUpOrCancelInternal}
                        onPointerDown={handlePointerDownInternal}
                        onPointerUp={handlePointerUpOrCancelInternal}
                    />
                )}

                {(clearable && value) && (
                    <div
                        className="text-input-clear"
                        onClick={handleClear}
                    >
                        &#x2715;
                    </div>
                )}

                {shouldShowPasswordToggle && (
                    <button
                        className="text-input-password-toggle"
                        aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                        type="button"
                        onClick={handlePasswordToggle}
                    >
                        <Icon
                            color={effectiveEndIconColor}
                            height={iconSize}
                            iconKey="visibility"
                            width={iconSize}
                        />
                    </button>
                )}

                {hasEndIcon && (
                    <div className="text-input-icon end">
                        {endIconKey ? (
                            <Icon
                                color={effectiveEndIconColor}
                                height={iconSize}
                                iconKey={endIconKey}
                                width={iconSize}
                            />
                        ) : (
                            <EndIcon
                                color={effectiveEndIconColor}
                                height={iconSize}
                                width={iconSize}
                            />
                        )}
                    </div>
                )}
            </div>

            {showCount && typeof maxLength === 'number' && (
                <div className="text-input-count">
                    {charCount} / {maxLength}
                </div>
            )}

            {helperText && (
                <div className={`text-input-helper${error ? ' error' : ''}`}>
                    {helperText}
                </div>
            )}
        </div>
    );
});

// Dev Tools
TextInput.displayName = 'TextInput';

export default TextInput;
